import { useEffect, useMemo, useState } from "react";
import {
  AddressFormValues,
  useOrderContext,
} from "../../hooks/useGenerateOrder";
import { useFormik } from "formik";
import * as Yup from "yup";

interface AddAddressProps {
  type: "billing" | "shipping"; // "billing" or "shipping"
  onAddressChange: (address: AddressFormValues) => void;
  initialValues?: AddressFormValues;
}

const AddAddress: React.FC<AddAddressProps> = ({
  type,
  onAddressChange,
  initialValues,
}) => {
  const { userAddresses } = useOrderContext();

  const addAddressValue = useFormik({
    initialValues: initialValues || {
      firstName: "",
      lastName: "",
      companyName: "",
      country: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      phone: "",
      email: "",
    },
    // (type === "billing" ? userAddresses.billing : userAddresses.shipping),
    enableReinitialize: false,
    validationSchema: Yup.object().shape({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      companyName: Yup.string(),
      country: Yup.string().required("Country is required"),
      streetAddress: Yup.string().required("Street address is required"),
      city: Yup.string().required("City is required"),
      state: Yup.string().required("State is required"),
      zipCode: Yup.string()
        .required("ZIP Code is required")
        .matches(/^\d{5}$/, "Invalid zip code format"),
      phone: Yup.string()
        .required("Phone is required")
        .matches(/^\+?\d{10,15}$/, "Invalid phone number format"),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
    }),
    onSubmit: (values) => {
      console.log("Address???", values);
    },
  });

  useEffect(() => {
    if (initialValues) {
      addAddressValue.resetForm({ values: initialValues });
      // Run a micro-task after reset to force onAddressChange
      setTimeout(() => {
        onAddressChange(initialValues);
      }, 0);
      // onAddressChange(initialValues);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    addAddressValue.setFieldValue(name, value);
    onAddressChange({ ...addAddressValue.values, [name]: value }); // Call the function to update address
  };

  return (
    <div>
      <div className="p-5">
        <p className="text-[16px] pb-2 font-semibold">
          {type === "billing" ? "Add Billing Address" : "Add Shipping Address"}
        </p>
        <hr />
        <form onSubmit={addAddressValue.handleSubmit}>
          <div className="grid pt-5 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label
                htmlFor="firstname"
                className="inline-block mb-2 text-base font-medium"
              >
                First Name
              </label>
              <input
                type="text"
                id="firstname"
                className="form-input"
                placeholder="First Name"
                name="firstName"
                onChange={handleChange}
                value={addAddressValue.values.firstName}
              />
              {addAddressValue.touched.firstName &&
                addAddressValue.errors.firstName && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.firstName}
                  </p>
                )}
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor="lastname"
                className="inline-block mb-2 text-base font-medium"
              >
                Last Name
              </label>
              <input
                type="text"
                id="lastname"
                className="form-input"
                placeholder="Last Name"
                name="lastName"
                onChange={handleChange}
                value={addAddressValue.values.lastName}
              />
              {addAddressValue.touched.lastName &&
                addAddressValue.errors.lastName && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.lastName}
                  </p>
                )}
            </div>
          </div>
          <div className="grid pt-3 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label
                htmlFor="companyname"
                className="inline-block mb-2 text-base font-medium"
              >
                Company Name
              </label>
              <input
                type="text"
                id="companyname"
                className="form-input"
                placeholder="Company Name"
                name="companyName"
                onChange={handleChange}
                value={addAddressValue.values.companyName}
              />
              {addAddressValue.touched.companyName &&
                addAddressValue.errors.companyName && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.companyName}
                  </p>
                )}
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor="country"
                className="inline-block mb-2 text-base font-medium"
              >
                Country
              </label>
              <input
                type="text"
                id="country"
                className="form-input"
                placeholder="Country"
                name="country"
                onChange={handleChange}
                value={addAddressValue.values.country}
              />
              {addAddressValue.touched.country &&
                addAddressValue.errors.country && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.country}
                  </p>
                )}
            </div>
          </div>
          <div className="grid pt-3 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label
                htmlFor="streetAddress"
                className="inline-block mb-2 text-base font-medium"
              >
                Street Address
              </label>
              <input
                type="text"
                id="streetAddress"
                className="form-input"
                placeholder="123 Main St"
                name="streetAddress"
                onChange={handleChange}
                value={addAddressValue.values.streetAddress}
              />
              {addAddressValue.touched.streetAddress &&
                addAddressValue.errors.streetAddress && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.streetAddress}
                  </p>
                )}
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor="city"
                className="inline-block mb-2 text-base font-medium"
              >
                City
              </label>
              <input
                type="text"
                id="city"
                className="form-input"
                placeholder="City"
                name="city"
                onChange={handleChange}
                value={addAddressValue.values.city}
              />
              {addAddressValue.touched.city && addAddressValue.errors.city && (
                <p className="text-sm text-red-500 mt-1">
                  {addAddressValue.errors.city}
                </p>
              )}
            </div>
          </div>
          <div className="grid pt-3 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label
                htmlFor="state"
                className="inline-block mb-2 text-base font-medium"
              >
                State
              </label>
              <input
                type="text"
                id="state"
                className="form-input"
                placeholder="State"
                name="state"
                onChange={handleChange}
                value={addAddressValue.values.state}
              />
              {addAddressValue.touched.state &&
                addAddressValue.errors.state && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.state}
                  </p>
                )}
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor="Zipcode"
                className="inline-block mb-2 text-base font-medium"
              >
                ZIP Code
              </label>
              <input
                type="text"
                id="Zipcode"
                className="form-input"
                placeholder="ZIP Code"
                name="zipCode"
                onChange={handleChange}
                value={addAddressValue.values.zipCode}
              />
              {addAddressValue.touched.zipCode &&
                addAddressValue.errors.zipCode && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.zipCode}
                  </p>
                )}
            </div>
          </div>
          <div className="grid pt-3 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label
                htmlFor="Phone"
                className="inline-block mb-2 text-base font-medium"
              >
                Phone
              </label>
              <input
                type="text"
                id="Phone"
                className="form-input"
                placeholder="Phone"
                name="phone"
                onChange={handleChange}
                value={addAddressValue.values.phone}
              />
              {addAddressValue.touched.phone &&
                addAddressValue.errors.phone && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.phone}
                  </p>
                )}
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor="Email"
                className="inline-block mb-2 text-base font-medium"
              >
                Email
              </label>
              <input
                type="text"
                id="Email"
                className="form-input"
                placeholder="Email"
                name="email"
                onChange={handleChange}
                value={addAddressValue.values.email}
              />
              {addAddressValue.touched.email &&
                addAddressValue.errors.email && (
                  <p className="text-sm text-red-500 mt-1">
                    {addAddressValue.errors.email}
                  </p>
                )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAddress;
